/**
 * Environment detection and validation utilities
 */

import { z } from 'zod';
import { ConfigurationError, stringToBoolean } from '@/types';

/**
 * Environment variable schema with validation
 */
export const EnvVarSchema = z.object({
  // Core configuration - Only 3 supported environment variables
  TESTDINO_RUNTIME: z
    .enum(['development', 'staging', 'production', 'test'])
    .optional(),
  TESTDINO_API_URL: z.string().url().optional(),
  TESTDINO_TOKEN: z.string().optional(),
  TESTDINO_TARGET_ENV: z.string().optional(),

  // CI/CD detection variables (auto-detected, not user-configurable)
  CI: z.string().optional(),
  GITHUB_ACTIONS: z.string().optional(),
  GITLAB_CI: z.string().optional(),
  JENKINS_URL: z.string().optional(),
  AZURE_HTTP_USER_AGENT: z.string().optional(),
  CIRCLECI: z.string().optional(),
  CODEBUILD_BUILD_ID: z.string().optional(),
  BITBUCKET_BUILD_NUMBER: z.string().optional(),
});

export type EnvVars = z.infer<typeof EnvVarSchema>;

/**
 * Environment type detection
 */
export enum EnvironmentType {
  DEVELOPMENT = 'development',
  STAGING = 'staging',
  PRODUCTION = 'production',
  TEST = 'test',
}

/**
 * CI/CD provider detection
 */
export enum CIProvider {
  GITHUB_ACTIONS = 'github-actions',
  GITLAB_CI = 'gitlab-ci',
  JENKINS = 'jenkins',
  AZURE_DEVOPS = 'azure-devops',
  CIRCLECI = 'circleci',
  AWS_CODEBUILD = 'aws-codebuild',
  BITBUCKET_PIPELINES = 'bitbucket-pipelines',
  UNKNOWN = 'unknown',
}

/**
 * Environment utilities class
 */
export class EnvironmentUtils {
  private static envCache: EnvVars | null = null;

  /**
   * Get and validate environment variables with caching
   */
  static getEnvironment(): EnvVars {
    if (this.envCache) {
      return this.envCache;
    }

    try {
      this.envCache = EnvVarSchema.parse(process.env);
      return this.envCache;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const issues = error.issues
          .map(issue => `${issue.path.join('.')}: ${issue.message}`)
          .join(', ');
        throw new ConfigurationError(
          `Invalid environment variables: ${issues}`,
          error
        );
      }
      throw new ConfigurationError(
        'Failed to validate environment',
        error as Error
      );
    }
  }

  /**
   * Clear environment cache (useful for testing)
   */
  static clearCache(): void {
    this.envCache = null;
  }

  /**
   * Detect current environment type
   */
  static detectEnvironmentType(): EnvironmentType {
    const env = this.getEnvironment();

    switch (env.TESTDINO_RUNTIME) {
      case 'development':
        return EnvironmentType.DEVELOPMENT;
      case 'staging':
        return EnvironmentType.STAGING;
      case 'production':
        return EnvironmentType.PRODUCTION;
      case 'test':
        return EnvironmentType.TEST;
      default:
        // Default to production for safety
        return EnvironmentType.PRODUCTION;
    }
  }

  /**
   * Detect CI/CD provider
   */
  static detectCIProvider(): CIProvider {
    const env = this.getEnvironment();

    if (env.GITHUB_ACTIONS) {
      return CIProvider.GITHUB_ACTIONS;
    }

    if (env.GITLAB_CI) {
      return CIProvider.GITLAB_CI;
    }

    if (env.JENKINS_URL) {
      return CIProvider.JENKINS;
    }

    if (env.AZURE_HTTP_USER_AGENT) {
      return CIProvider.AZURE_DEVOPS;
    }

    if (env.CIRCLECI) {
      return CIProvider.CIRCLECI;
    }

    if (env.CODEBUILD_BUILD_ID) {
      return CIProvider.AWS_CODEBUILD;
    }

    if (env.BITBUCKET_BUILD_NUMBER) {
      return CIProvider.BITBUCKET_PIPELINES;
    }

    return CIProvider.UNKNOWN;
  }

  /**
   * Check if running in CI environment
   */
  static isCI(): boolean {
    const env = this.getEnvironment();
    return (
      stringToBoolean(env.CI) || this.detectCIProvider() !== CIProvider.UNKNOWN
    );
  }

  /**
   * Check if in development mode
   */
  static isDevelopment(): boolean {
    return this.detectEnvironmentType() === EnvironmentType.DEVELOPMENT;
  }

  /**
   * Check if in production mode
   */
  static isProduction(): boolean {
    return this.detectEnvironmentType() === EnvironmentType.PRODUCTION;
  }

  /**
   * Check if in test mode
   */
  static isTest(): boolean {
    return this.detectEnvironmentType() === EnvironmentType.TEST;
  }

  /**
   * Get string value from environment variable
   */
  static getStringEnv(
    key: keyof EnvVars,
    defaultValue?: string
  ): string | undefined {
    const env = this.getEnvironment();
    return env[key] || defaultValue;
  }

  /**
   * Get environment summary for debugging
   */
  static getEnvironmentSummary(): Record<string, unknown> {
    const env = this.getEnvironment();

    return {
      type: this.detectEnvironmentType(),
      ci: this.isCI(),
      provider: this.detectCIProvider(),
      runtime: env.TESTDINO_RUNTIME,
      hasToken: !!env.TESTDINO_TOKEN,
      hasApiUrl: !!env.TESTDINO_API_URL,
      hasTargetEnv: !!env.TESTDINO_TARGET_ENV,
    };
  }
}
